import { useState, useRef, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
// import { useFormik } from 'formik';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCamera, faSpinner, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../services/userService';
import { getUserProfile } from '../../services/userService';
import { uploadProfilePhoto } from '../../services/storageService';
// import * as Yup from 'yup';
import './ProfileSetup.css';

interface ProfileSetupProps {
  onComplete: () => void;
}

interface ProfileData {
  displayName: string;
  bio: string;
  location: string;
  phoneNumber: string;
  dateOfBirth: string;
  pronouns: string;
  photoURL: string | null;
  smsOptIn: boolean;
}

export default function ProfileSetup({ onComplete }: ProfileSetupProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState('');
  const [smsAutoSelected, setSmsAutoSelected] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [profileData, setProfileData] = useState<ProfileData>({
    displayName: currentUser?.displayName || '',
    bio: '',
    location: '',
    phoneNumber: '',
    dateOfBirth: '',
    pronouns: '',
    photoURL: currentUser?.photoURL || null,
    smsOptIn: false,
  });

  const totalSteps = 4;

  // Validation schema - removed to use manual validation
  // The current implementation uses debounced auto-save instead of form submission
  // Manual validation is performed inline for phone numbers and other fields

  // Load initial profile data from database
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) {
        setLoadingProfile(false);
        return;
      }

      try {
        const userProfile = await getUserProfile(currentUser.uid);

        if (userProfile) {
          setProfileData({
            displayName: userProfile.profile.displayName || currentUser.displayName || '',
            bio: userProfile.profile.bio || '',
            location: userProfile.profile.location || '',
            phoneNumber: userProfile.profile.phoneNumber || '',
            dateOfBirth: userProfile.profile.dateOfBirth || '',
            pronouns: userProfile.profile.pronouns || '',
            photoURL: userProfile.profile.photoURL || currentUser.photoURL || null,
            smsOptIn: userProfile.preferences.notifications.sms || false,
          });

          // Set smsAutoSelected if user already has SMS enabled
          if (userProfile.preferences.notifications.sms) {
            setSmsAutoSelected(true);
          }
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        // Don't show error, just use defaults
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    if (!phoneNumber || !phoneNumber.trim()) {
      return true; // Empty phone number is valid (optional field)
    }

    try {
      const parsed = parsePhoneNumber(phoneNumber, 'US');
      return parsed ? parsed.isValid() : false;
    } catch {
      return false;
    }
  };

  const saveProfileData = async (data: Partial<ProfileData>) => {
    if (!currentUser) {
      console.warn('Cannot save profile: No current user');
      return;
    }

    setError('');

    try {
      const updates: Record<string, unknown> = {};

      // Profile updates
      if (data.displayName !== undefined || data.bio !== undefined ||
        data.location !== undefined || data.phoneNumber !== undefined ||
        data.dateOfBirth !== undefined || data.pronouns !== undefined ||
        data.photoURL !== undefined) {
        updates.profile = {};
        if (data.displayName !== undefined) (updates.profile as Record<string, unknown>).displayName = data.displayName || null;
        if (data.bio !== undefined) (updates.profile as Record<string, unknown>).bio = data.bio || null;
        if (data.location !== undefined) (updates.profile as Record<string, unknown>).location = data.location || null;

        // Only save phone number if it's valid or empty
        if (data.phoneNumber !== undefined) {
          if (validatePhoneNumber(data.phoneNumber)) {
            (updates.profile as Record<string, unknown>).phoneNumber = data.phoneNumber || null;
            setPhoneNumberError('');
          } else {
            setPhoneNumberError(t('profileSetup.step1.phoneNumberInvalid'));
            console.warn('Phone number validation failed, skipping phone number field');
            // Skip phone number but continue saving other fields
          }
        }

        if (data.dateOfBirth !== undefined) (updates.profile as Record<string, unknown>).dateOfBirth = data.dateOfBirth || null;
        if (data.pronouns !== undefined) (updates.profile as Record<string, unknown>).pronouns = data.pronouns || null;
        if (data.photoURL !== undefined) (updates.profile as Record<string, unknown>).photoURL = data.photoURL;
      }

      // Preference updates (SMS opt-in)
      if (data.smsOptIn !== undefined) {
        updates.preferences = {
          notifications: {
            sms: data.smsOptIn,
          },
        };
      }

      // Only save if there are actual updates
      if (Object.keys(updates).length === 0) {
        console.log('No valid updates to save');
        return;
      }

      console.log('Saving profile updates to Firestore:', updates);
      await updateUserProfile(currentUser.uid, updates);
      console.log('✓ Profile saved successfully to Firestore');
    } catch (err) {
      console.error('✗ Failed to save profile to Firestore:', err);
      // Don't show error for auto-saves, only log
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Format phone number as user types
    let formattedValue = value;
    if (name === 'phoneNumber') {
      const formatter = new AsYouType('US');
      formattedValue = formatter.input(value);
    }

    // Auto-select SMS opt-in only once when phone number changes from blank to a value
    const shouldAutoSelectSms =
      name === 'phoneNumber' &&
      !profileData.phoneNumber.trim() &&
      formattedValue.trim() &&
      !smsAutoSelected;

    if (shouldAutoSelectSms) {
      setSmsAutoSelected(true);
      const newData = {
        ...profileData,
        [name]: formattedValue,
        smsOptIn: true,
      };
      setProfileData(newData);

      // Debounce save by 1 second
      saveTimeoutRef.current = setTimeout(() => {
        saveProfileData({ [name]: formattedValue, smsOptIn: true });
      }, 1000);
    } else {
      setProfileData({
        ...profileData,
        [name]: formattedValue,
      });

      // Debounce save by 1 second
      saveTimeoutRef.current = setTimeout(() => {
        saveProfileData({ [name]: formattedValue });
      }, 1000);
    }
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setProfileData({
      ...profileData,
      [name]: checked,
    });

    // Save checkbox changes immediately
    saveProfileData({ [name]: checked });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    if (!file.type.startsWith('image/')) {
      setError(t('profileSetup.errors.invalidImage'));
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('profileSetup.errors.imageTooLarge'));
      return;
    }

    setError('');
    setUploadingPhoto(true);

    try {
      const downloadURL = await uploadProfilePhoto(currentUser.uid, file);
      setProfileData({ ...profileData, photoURL: downloadURL });

      // Save photo URL immediately
      await saveProfileData({ photoURL: downloadURL });
    } catch (err) {
      console.error('Failed to upload photo:', err);
      setError(err instanceof Error ? err.message : t('profileSetup.errors.uploadFailed'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleNext = async () => {
    if (step < totalSteps) {
      // Clear any pending debounced saves
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Save current step data before moving forward
      await saveProfileData(profileData);

      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError('');

    try {
      // Clear any pending debounced saves
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Final save with profileCompleted flag
      await updateUserProfile(currentUser.uid, {
        metadata: {
          profileCompleted: true,
        },
      });

      // Move to success step
      setStep(totalSteps);
    } catch (err) {
      console.error('Failed to complete profile setup:', err);
      setError(t('profileSetup.errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const handleFinish = () => {
    onComplete();
  };

  const calculateProgress = () => {
    const fields = [
      profileData.displayName.trim(),
      profileData.phoneNumber.trim(),
      profileData.photoURL,
      profileData.bio.trim(),
      profileData.location.trim(),
      profileData.dateOfBirth.trim(),
      profileData.pronouns.trim(),
    ];
    const filledFields = fields.filter(field => field).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const canProceedStep1 = profileData.displayName.trim().length > 0;

  if (loadingProfile) {
    return (
      <div className="profile-setup">
        <div className="setup-loading">
          <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
          <p>{t('profile.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-setup">
      {/* Progress Bar */}
      <div className="profile-progress-container">
        <div className="profile-progress-bar">
          <div className="profile-progress-fill" style={{ width: `${calculateProgress()}%` }}></div>
        </div>
        <div className="profile-progress-text">{calculateProgress()}{t('profileSetup.progressComplete')}</div>
      </div>

      {/* Progress Indicator */}
      <div className="progress-indicator">
        {[1, 2, 3, 4].map((stepNum) => (
          <div
            key={stepNum}
            className={`step ${step >= stepNum ? 'active' : ''} ${step > stepNum ? 'completed' : ''}`}
          >
            <div className="step-number">
              {step > stepNum ? <FontAwesomeIcon icon={faCheck} /> : stepNum}
            </div>
            <div className="step-label">
              {stepNum === 1 && t('profileSetup.steps.basicInfo')}
              {stepNum === 2 && t('profileSetup.steps.profileDetails')}
              {stepNum === 3 && t('profileSetup.steps.review')}
              {stepNum === 4 && t('profileSetup.steps.complete')}
            </div>
          </div>
        ))}
      </div>

      {error && <div className="setup-error">{error}</div>}

      {/* Step 1: Basic Information */}
      {step === 1 && (
        <div className="setup-step active">
          <h2>{t('profileSetup.step1.title')}</h2>
          <p className="step-description">{t('profileSetup.step1.description')}</p>

          <div className={`form-group ${!profileData.displayName.trim() ? 'incomplete' : ''}`}>
            <label htmlFor="displayName" className="required">
              {t('profileSetup.step1.displayName')}
              {!profileData.displayName.trim() && <span className="field-indicator" title="Required field"></span>}
            </label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={profileData.displayName}
              onChange={handleInputChange}
              placeholder={t('profileSetup.step1.displayNamePlaceholder')}
              required
            />
          </div>

          <div className={`form-group ${!profileData.phoneNumber.trim() ? 'incomplete' : ''}`}>
            <label htmlFor="phoneNumber" className="required">
              {t('profileSetup.step1.phoneNumber')}
              {!profileData.phoneNumber.trim() && <span className="field-indicator" title="Required field"></span>}
            </label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={profileData.phoneNumber}
              onChange={handleInputChange}
              placeholder={t('profileSetup.step1.phoneNumberPlaceholder')}
            />
            <span className="field-hint">{t('profileSetup.step1.phoneNumberHint')}</span>
            {phoneNumberError && <span className="error-message">{phoneNumberError}</span>}

            <div className="checkbox-group">
              <label htmlFor="smsOptIn" className="checkbox-label">
                <span dangerouslySetInnerHTML={{ __html: t('profileSetup.step1.smsOptIn') }}></span>
              </label>
              <input
                type="checkbox"
                id="smsOptIn"
                name="smsOptIn"
                checked={profileData.smsOptIn}
                onChange={handleCheckboxChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="dateOfBirth">
              {t('profileSetup.step1.dateOfBirth')}
            </label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={profileData.dateOfBirth}
              onChange={handleInputChange}
            />
            <span className="field-hint">{t('profileSetup.step1.dateOfBirthHint')}</span>
          </div>

          <div className="form-group">
            <label htmlFor="pronouns">
              {t('profileSetup.step1.pronouns')}
            </label>
            <input
              type="text"
              id="pronouns"
              name="pronouns"
              value={profileData.pronouns}
              onChange={handleInputChange}
              placeholder={t('profileSetup.step1.pronounsPlaceholder')}
            />
            <span className="field-hint">{t('profileSetup.step1.pronounsHint')}</span>
          </div>
        </div>
      )}

      {/* Step 2: Profile Details */}
      {step === 2 && (
        <div className="setup-step active">
          <h2>{t('profileSetup.step2.title')}</h2>
          <p className="step-description">{t('profileSetup.step2.description')}</p>

          <div className="form-group">
            <label>{t('profileSetup.step2.profilePicture')}</label>
            <div className="avatar-upload-section">
              <div className="avatar-preview" onClick={() => document.getElementById('photoUpload')?.click()}>
                {profileData.photoURL ? (
                  <img src={profileData.photoURL} alt="Profile" className="avatar-image" />
                ) : (
                  <div className="avatar-placeholder">
                    <FontAwesomeIcon icon={faUser} className="avatar-icon" />
                  </div>
                )}
                <div className="avatar-overlay">
                  {uploadingPhoto ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="upload-icon" />
                  ) : (
                    <FontAwesomeIcon icon={faCamera} className="upload-icon" />
                  )}
                </div>
              </div>
              <label htmlFor="photoUpload" className="visually-hidden">
                {t('profileSetup.step2.uploadPhoto')}
              </label>
              <input
                type="file"
                id="photoUpload"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="avatar-file-input"
                disabled={uploadingPhoto}
              />
              <span className="field-hint">{t('profileSetup.step2.uploadHint')}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="bio">{t('profileSetup.step2.bio')}</label>
            <textarea
              id="bio"
              name="bio"
              value={profileData.bio}
              onChange={handleInputChange}
              placeholder={t('profileSetup.step2.bioPlaceholder')}
              rows={4}
              maxLength={500}
            />
            <span className="char-count">{profileData.bio.length}/500</span>
          </div>

          <div className="form-group">
            <label htmlFor="location">{t('profileSetup.step2.location')}</label>
            <input
              type="text"
              id="location"
              name="location"
              value={profileData.location}
              onChange={handleInputChange}
              placeholder={t('profileSetup.step2.locationPlaceholder')}
            />
            <span className="field-hint">{t('profileSetup.step2.locationHint')}</span>
          </div>
        </div>
      )}

      {/* Step 3: Preferences */}
      {step === 3 && (
        <div className="setup-step active">
          <h2>{t('profileSetup.step3.title')}</h2>
          <p className="step-description">{t('profileSetup.step3.description')}</p>

          <div className="profile-summary">
            <div className="summary-item">
              <div className="summary-avatar">
                {profileData.photoURL ? (
                  <img src={profileData.photoURL} alt="Profile" />
                ) : (
                  <div className="avatar-placeholder-small">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                )}
              </div>
            </div>
            <div className="summary-item">
              <strong>{t('profileSetup.step3.displayName')}</strong>
              <span>{profileData.displayName || t('profileSetup.step3.notProvided')}</span>
            </div>
            {profileData.phoneNumber && (
              <div className="summary-item">
                <strong>{t('profileSetup.step3.phone')}</strong>
                <span>{profileData.phoneNumber}</span>
              </div>
            )}
            {profileData.bio && (
              <div className="summary-item">
                <strong>{t('profileSetup.step3.bio')}</strong>
                <span>{profileData.bio}</span>
              </div>
            )}
            {profileData.location && (
              <div className="summary-item">
                <strong>{t('profileSetup.step3.location')}</strong>
                <span>{profileData.location}</span>
              </div>
            )}
          </div>

          <p className="review-note">
            {t('profileSetup.step3.note')}
          </p>
        </div>
      )}

      {/* Step 4: Success */}
      {step === 4 && (
        <div className="setup-step active success-step">
          <div className="success-icon-large">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <h2>{t('profileSetup.step4.title')}</h2>
          <p className="success-message">{t('profileSetup.step4.message')}</p>
          <button type="button" onClick={handleFinish} className="btn-primary btn-large">
            {t('profileSetup.step4.getStarted')}
          </button>
        </div>
      )}

      {/* Navigation Buttons */}
      {step < totalSteps && (
        <div className="setup-actions">
          <div className="actions-left">
            {step > 1 && (
              <button type="button" onClick={handleBack} className="btn-secondary" disabled={loading}>
                {t('profileSetup.buttons.back')}
              </button>
            )}
          </div>
          <div className="actions-right">
            <button type="button" onClick={handleSkip} className="btn-text" disabled={loading}>
              {t('profileSetup.buttons.skip')}
            </button>
            {step < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary"
                disabled={(step === 1 && !canProceedStep1) || loading}
              >
                {t('profileSetup.buttons.next')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleComplete}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? t('profileSetup.buttons.saving') : t('profileSetup.buttons.complete')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
